// frontend/src/components/TelegramSettingsCard.jsx
import React, { useEffect, useState } from 'react';
import axiosAuth from '../axiosConfig';

export default function TelegramSettingsCard() {
  const [status, setStatus] = useState(null);
  const [link, setLink] = useState('');
  const [loading, setLoading] = useState(false);

  // optional: username fallback (only if you implemented link-by-username API)
  const [tgUsername, setTgUsername] = useState('');
  const [usernameMsg, setUsernameMsg] = useState('');

  const loadStatus = async () => {
    try {
      const r = await axiosAuth.get('/notifications/telegram/status');
      setStatus(r.data.telegram || {});
    } catch (e) {
      console.error('telegram status error', e);
    }
  };

  useEffect(() => {
    loadStatus();
  }, []);

  const requestLink = async () => {
    setLoading(true);
    setLink('');
    try {
      const r = await axiosAuth.post('/notifications/telegram/request-link');
      setLink(r.data.deepLink);
    } catch (e) {
      console.error('request-link error', e);
      alert(e?.response?.data?.error || 'Could not create link. Try again.');
    } finally {
      setLoading(false);
    }
  };

  const enable = async () => {
    try {
      await axiosAuth.post('/notifications/telegram/enable');
      await loadStatus();
    } catch (e) {
      alert(e?.response?.data?.error || 'Enable failed');
    }
  };

  const disable = async () => {
    try {
      await axiosAuth.post('/notifications/telegram/disable');
      await loadStatus();
    } catch (e) {
      alert(e?.response?.data?.error || 'Disable failed');
    }
  };

  // optional: username fallback flow (only if you built /link-by-username)
  const linkByUsername = async () => {
    setUsernameMsg('');
    const u = (tgUsername || '').trim();
    if (!u) return setUsernameMsg('Enter your @username first.');
    try {
      const r = await axiosAuth.post('/notifications/telegram/link-by-username', { username: u });
      setUsernameMsg('Now open the bot and press Start within 15 minutes.');
      setLink(r.data.openBotUrl);
    } catch (e) {
      setUsernameMsg(e?.response?.data?.error || 'Failed to start username linking.');
    }
  };

  const linked = Boolean(status?.chatId);
  const enabled = Boolean(status?.enabled);
  const uname = status?.username ? '@' + status.username : null;

  return (
    <div className="bg-white border rounded-2xl p-6 shadow-sm">
      <h3 className="text-xl font-semibold text-gray-800 mb-1">Telegram Alerts</h3>
      <p className="text-gray-600 mb-4">
        Receive P2P notifications (new trade, messages, paid, released, disputes) in Telegram.
      </p>

      <div className="mb-4">
        <div className="text-sm text-gray-700">
          Status:&nbsp;
          {linked ? (
            <span className="text-green-700 font-medium">Linked</span>
          ) : (
            <span className="text-red-700 font-medium">Not linked</span>
          )}
        </div>
        {linked && (
          <div className="text-sm text-gray-500">
            Account: {uname || 'unknown'} • Delivery: {enabled ? 'Enabled' : 'Disabled'}
          </div>
        )}
      </div>

      {!linked && (
        <div className="mb-4">
          <button
            onClick={requestLink}
            disabled={loading}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
          >
            {loading ? 'Creating link…' : 'Connect Telegram'}
          </button>

          {link && (
            <div className="mt-3 space-y-2">
              <button
                onClick={() => {
                  // best mobile behavior: open in same tab
                  window.location.href = link;
                }}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
              >
                Open Telegram and press Start
              </button>

              <button
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(link);
                    alert('Link copied. Paste it in Telegram if it doesn’t open.');
                  } catch (e) {
                    // fallback for older browsers
                    const el = document.createElement('textarea');
                    el.value = link;
                    document.body.appendChild(el);
                    el.select();
                    document.execCommand('copy');
                    document.body.removeChild(el);
                    alert('Link copied. Paste it in Telegram if it doesn’t open.');
                  }
                }}
                className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded-lg"
              >
                Copy Link
              </button>

              <p className="text-xs text-gray-500">
                After pressing <b>Start</b> in Telegram, come back and refresh this page.
              </p>
            </div>
          )}

          <details className="mt-4">
            <summary className="cursor-pointer text-sm text-gray-700">
              Trouble with deep link? Connect by username (fallback)
            </summary>
            <div className="mt-2">
              <div className="flex gap-2">
                <input
                  value={tgUsername}
                  onChange={(e) => setTgUsername(e.target.value)}
                  placeholder="@yourhandle"
                  className="border rounded-lg px-3 py-2 text-sm flex-1"
                />
                <button
                  onClick={linkByUsername}
                  className="bg-gray-800 text-white px-3 py-2 rounded-lg text-sm"
                >
                  Save username
                </button>
              </div>
              {usernameMsg && <div className="text-xs text-gray-600 mt-2">{usernameMsg}</div>}
            </div>
          </details>
        </div>
      )}

      {linked && (
        <div className="flex gap-2">
          {!enabled && (
            <button
              onClick={enable}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg"
            >
              Enable Alerts
            </button>
          )}
          {enabled && (
            <button
              onClick={disable}
              className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded-lg"
            >
              Disable Alerts
            </button>
          )}
        </div>
      )}

      <p className="text-xs text-gray-500 mt-4">
        You don’t need to share a Telegram ID or phone number. Pressing Start in the bot securely links your account.
      </p>
    </div>
  );
}
